/*
 * File: PaidUserSignUpStep.jsx
 * Project: lets-talk-web
 *
 * Created by Morgan Rivera on April 3, 2026 at 1:15 PM
 * Copyright © 2022 Let's Talk. All rights reserved.
 */

/**
 * Imports
 */

// Modules
import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';

// Utilities
import { toastError, toastSuccess } from '../../../../utilities/toaster';


// Services
import { waitlistUser } from '../../../../services/authentication';

// Components
import {
	Button, TextInput, Typography
} from '../../../../components';

// Styles
import * as S from './PaidUserSignUpStep.styles';


/**
 * Component
 */

export const PaidUserSignUpStep = forwardRef(({
	className
}, ref) => {

	// Create state handlers
	const [emailError, setEmailError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [inputValues, setInputValues] = useState({});

	// Get actions from hooks
	const navigate = useNavigate();

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get current locale from hook
	const stateLocale = useSelector((state) => state.locale.value);

	// Handle create account function
	const handleCreateAccount = async () => {

		// Get parameters
		const {
			email, termsAgreed
		} = inputValues;

		// Validate parameters
		if (!email || !validator.isEmail(email)) {
			setEmailError({ message: 'Please enter your email' });
			return;
		}
		if (!termsAgreed) {
			toastError(uiMode, 'You must agree to our Terms of Service before continuing.', { duration: 7000 });
			return;
		}


		// Add to waitlist for admin approval
		waitlistUser({ email }).then(() => {

			// Set loading state
			setIsLoading(false);

			// Send notification
			toastSuccess(uiMode, 'We currently have a waitlist. You have been added! As soon as a new spot is available, we will email you.', { duration: 7000 });


		}).catch(({ response }) => {

			// Set loading state
			setIsLoading(false);
			setEmailError(null);

			// Show error message
			if (response?.data?.message) {
				toastError(uiMode, response?.data?.message, { duration: 7000 });
			} else {
				toastError(uiMode, 'Whoops. We\'re having trouble. Please try again.', { duration: 7000 });
			}
		});

		// Set loading state
		setIsLoading(true);

	};

	// Handle on input change action
	const handleOnChange = (event) => {
		const {
			name, value, checked, type
		} = event.target;
		if (type === 'checkbox') {
			setInputValues({ ...inputValues, [name]: checked });
		} else {
			setInputValues({ ...inputValues, [name]: value });
		}
	};


	// Handle login action function
	const loginFunction = () => {
		navigate(`${stateLocale.localePath}/login`);
	};

	// Handle component initialization
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>

			{/* Content */}
			<Typography tag="h2" weight="bold">Sign Up</Typography>
			<Typography tag="p" variation="1" weight="regular">Sign up to get started! You&apos;ll be added to our waitlist, and we&apos;ll notify you by email when your access is ready.</Typography>

			{/* Inputs */}
			<TextInput
				label="Email Address"
				name="email"
				type="text"
				placeholder="alex@acme.com"
				error={emailError}
				value={inputValues.email || ''}
				containerClassName="inputSpacer"
				autoComplete="email"
				onFocus={() => { setEmailError(null); }}
				onKeyUp={() => { setEmailError(null); }}
				onBlur={() => { setEmailError(null); }}
				onChange={handleOnChange}
			/>

			{/* Checkboxes */}
			{/* <S.CheckboxContainer>
				<S.CheckboxRow>
					<Checkbox
						name="termsAgreed"
						value={inputValues.termsAgreed || false}
						onChange={handleOnChange}
						isDark
					/>
					<Typography variation="2" weight="regular">
						I have read and agree to the Let&apos;s Talk
						{' '}
						<LocaleLink to="/legal/terms" target="_blank">Terms of Service</LocaleLink>
						{' and '}
						<LocaleLink to="/legal/privacy" target="_blank">Privacy Policy</LocaleLink>
					</Typography>
				</S.CheckboxRow>
			</S.CheckboxContainer> */}

			{/* Continue Button */}
			<Button disabled={isLoading} isLoading={isLoading} onClick={handleCreateAccount} size="large">Sign Up</Button>


			{/* Login Button */}
			<S.BottomActionContainer>
				<Button variant="text" size="small" onClick={loginFunction} disabled={isLoading}>
					<Typography tag="p" variation="2" weight="medium">
						Already have an account?
						{' '}
						<Typography weight="bold">
							Log In
						</Typography>
					</Typography>
				</Button>
			</S.BottomActionContainer>
		</S.StepContainer>
	);
});


/**
 * Configuration
 */

PaidUserSignUpStep.displayName = 'PaidUserSignUpStep';
PaidUserSignUpStep.propTypes = {
	className: PropTypes.string
};
PaidUserSignUpStep.defaultProps = {
	className: null
};
